import { useEffect, useState } from "react";
import { getSessionState, clearSessionState, SESSION_PHASE } from "utils/sessionPersistence";
import { checkSessionStatus, redirectToGroupCode, REDIRECT_ERROR } from "components/pages/Session/sessionPrefetch";

export const useSessionRehydration = ({ authUserData, isStart, isSessionReady, prefetchedResponse, setIsStart, currentEventState }) => {
  const [isRehydrating, setIsRehydrating] = useState(false);

  // Rehydrate session from sessionStorage on refresh
  useEffect(() => {
    const persisted = getSessionState();
    if (!persisted?.phase || !persisted?.groupCode || !authUserData) return;
    // Only rehydrate if we're not already in a session
    if (isStart || isSessionReady || prefetchedResponse) return;

    const rehydrate = async () => {
      if (persisted.phase === SESSION_PHASE.ACTIVE && persisted.sessionId) {
        setIsRehydrating(true);
        try {
          // Validate session is still active before re-joining
          const { isActive, redirectError } = await checkSessionStatus({
            sessionId: persisted.sessionId,
            groupCode: persisted.groupCode,
            userId: authUserData?.id,
            source: "rehydration",
          });
          if (!isActive) {
            clearSessionState();
            redirectToGroupCode(persisted.groupCode, redirectError, {
              source: "rehydration",
              sessionId: persisted.sessionId,
            });
            return;
          }
          // Session is still active — trigger the full join + connect flow
          setIsStart(true);
        } catch {
          clearSessionState();
          redirectToGroupCode(persisted.groupCode, REDIRECT_ERROR.RETRY_FAILURE, {
            source: "rehydration",
            sessionId: persisted.sessionId,
          });
        } finally {
          setIsRehydrating(false);
        }
      }
      // phase === "tech_check" — no special action needed.
      // The existing prefetch flow will run once devicesReady is true.
    };

    rehydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserData]);

  // Clear persisted session when session completes
  useEffect(() => {
    if (currentEventState === "complete") {
      clearSessionState();
    }
  }, [currentEventState]);

  return { isRehydrating };
};
